"use client";

import { c as _c } from "react/compiler-runtime";
import Script from "next/script";
import { useEffect, useRef } from "react";
import { useDemoStore } from "@/stores/demo-store";
interface TurnstileAPI {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (widgetId?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

/**
 * Cloudflare Turnstile widget for the demo. Renders only when
 * NEXT_PUBLIC_TURNSTILE_SITE_KEY is set — otherwise it's a no-op and the server
 * skips verification, so local dev and previews work without Cloudflare keys.
 *
 * Uses the "interaction-only" appearance so the widget stays invisible unless a
 * challenge is required. The solved token is written to the demo store and
 * attached to each AI request. Turnstile tokens are SINGLE-USE, so when a
 * request consumes the token (store value cleared to null), this widget resets
 * to mint a fresh one for the next request.
 */
export function DemoTurnstile() {
  const $ = _c(7);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const setToken = useDemoStore(_temp);
  const token = useDemoStore(_temp2);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const hadTokenRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== setToken) {
    t0 = () => {
      if (!siteKey) {
        return;
      }
      let cancelled = false;
      const tryRender = () => {
        if (cancelled) {
          return;
        }
        if (window.turnstile && containerRef.current && widgetIdRef.current === null) {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: t => setToken(t),
            "expired-callback": () => setToken(null),
            "error-callback": () => setToken(null),
            appearance: "interaction-only"
          });
        } else {
          if (widgetIdRef.current === null) {
            setTimeout(tryRender, 300);
          }
        }
      };
      tryRender();
      return () => {
        cancelled = true;
      };
    };
    t1 = [siteKey, setToken];
    $[0] = setToken;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== token) {
    t2 = () => {
      if (!siteKey) {
        return;
      }
      if (token) {
        hadTokenRef.current = true;
      } else {
        if (hadTokenRef.current && window.turnstile && widgetIdRef.current !== null) {
          hadTokenRef.current = false;
          window.turnstile.reset(widgetIdRef.current);
        }
      }
    };
    t3 = [siteKey, token];
    $[3] = token;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!siteKey) {
    return null;
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <><Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="afterInteractive" /><div ref={containerRef} className="fixed bottom-20 left-3 z-[60] lg:bottom-3" /></>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp2(s_0) {
  return s_0.turnstileToken;
}
function _temp(s) {
  return s.setTurnstileToken;
}